import { OnScreenModal } from '@/components/ui/OnScreenModal'
import type { StatusMappingTree } from '../../types'

function nextStepsAsList(raw: string[] | Record<string, string> | undefined): string[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  return Object.values(raw)
}

export type ResidentMultiEditInfoModalType = 'resident_status' | 'resident_progress'

export interface ResidentMultiEditInfoModalProps {
  isOpen: boolean
  onClose: () => void
  type: ResidentMultiEditInfoModalType
  residentStatusMapping?: StatusMappingTree
  selectedStatus?: string
}

export function ResidentMultiEditInfoModal({
  isOpen,
  onClose,
  type,
  residentStatusMapping,
  selectedStatus,
}: ResidentMultiEditInfoModalProps) {
  const selectedEntry = selectedStatus ? residentStatusMapping?.[selectedStatus] : undefined
  const title =
    type === 'resident_status'
      ? selectedEntry?.name
        ? `Resident Status — ${selectedEntry.name}`
        : 'Resident Status'
      : selectedEntry?.name
        ? `Resident Progress — ${selectedEntry.name}`
        : 'Resident Progress'

  return (
    <OnScreenModal isOpen={isOpen} title={title} onClose={onClose} maxWidth="max-w-4xl">
      {type === 'resident_status' ? (
        <ResidentStatusInstructions
          mapping={residentStatusMapping}
          selectedStatus={selectedStatus}
        />
      ) : (
        <ResidentProgressInstructions
          mapping={residentStatusMapping}
          selectedStatus={selectedStatus}
        />
      )}
    </OnScreenModal>
  )
}

function ResidentStatusInstructions({
  mapping,
  selectedStatus,
}: {
  mapping?: StatusMappingTree
  selectedStatus?: string
}) {
  const selectedEntry = selectedStatus ? mapping?.[selectedStatus] : undefined
  const nextSteps = nextStepsAsList(selectedEntry?.next_step)

  return (
    <div className="space-y-4 text-sm text-slate-800">
      {nextSteps.length > 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
          <p className="mb-2 font-semibold">
            {selectedEntry?.name ? `${selectedEntry.name}:` : 'Instructions:'}
          </p>
          <ul className="list-disc space-y-1 pl-5">
            {nextSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <ResidentStatusMappingTable mapping={mapping} />
    </div>
  )
}

function ResidentProgressInstructions({
  mapping,
  selectedStatus,
}: {
  mapping?: StatusMappingTree
  selectedStatus?: string
}) {
  const selectedEntry = selectedStatus ? mapping?.[selectedStatus] : undefined
  const progressEntries = Object.entries(selectedEntry?.progress ?? {})
  const nextSteps = nextStepsAsList(selectedEntry?.next_step)

  return (
    <div className="space-y-4 text-sm text-slate-800">
      {selectedStatus && progressEntries.length > 0 ? (
        <div className="space-y-2">
          <p className="font-semibold text-slate-900">Available progress values</p>
          <ul className="list-disc space-y-1 pl-5">
            {progressEntries.map(([code, label]) => (
              <li key={code}>
                <span className="font-medium">{label}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-slate-600">Select a resident status to view progress guidance.</p>
      )}

      {nextSteps.length > 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
          <p className="mb-2 font-semibold">Next steps</p>
          <ul className="list-disc space-y-1 pl-5">
            {nextSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <ResidentStatusMappingTable mapping={mapping} />
    </div>
  )
}

function ResidentStatusMappingTable({ mapping }: { mapping?: StatusMappingTree }) {
  if (!mapping || Object.keys(mapping).length === 0) {
    return null
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full rounded border border-slate-200 text-xs">
        <thead className="border-b bg-slate-100 font-semibold text-slate-700">
          <tr>
            <th className="border-r p-2 text-left">Resident Status</th>
            <th className="p-2 text-left">Resident Progress</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {Object.entries(mapping).map(([statusCode, entry]) => {
            const progressEntries = Object.entries(entry.progress ?? {})
            const statusName = entry.name ?? statusCode

            if (progressEntries.length === 0) {
              return (
                <tr key={statusCode}>
                  <td className="border-r p-2 align-top">{statusName}</td>
                  <td className="p-2 align-top">—</td>
                </tr>
              )
            }

            return progressEntries.map(([progressCode, progressLabel], index) => (
              <tr key={`${statusCode}-${progressCode}`}>
                {index === 0 ? (
                  <td className="border-r p-2 align-top" rowSpan={progressEntries.length}>
                    {statusName}
                  </td>
                ) : null}
                <td className="p-2 align-top">{progressLabel}</td>
              </tr>
            ))
          })}
        </tbody>
      </table>
    </div>
  )
}
